import type {
  CSSProperties,
  ChangeEvent,
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
  Ref,
} from 'react';
import { IcnCheck } from '../../icons/index.js';
import { useI18nT } from '../../i18n/index.js';
import { MOBILE_FLOATING_PANEL_STYLE } from '../shared/mobileFloating.js';
import type { SelectGroup, SelectOption } from './types.js';
import { optionLabelText } from './utils.js';

export interface SelectPanelProps<V extends string = string> {
  panelId: string;
  triggerId: string;
  floatingStyle: CSSProperties;
  maxHeight: number;
  placement: 'top' | 'bottom';
  mobileSheet: boolean;
  options: readonly SelectOption<V>[];
  groupOrder: readonly { group: SelectGroup | null; optionIndices: number[] }[];
  visibleCount: number;
  highlightIndex: number;
  selectedIndex: number;
  searchable: boolean;
  searchPlaceholder: string;
  query: string;
  ariaLabelledBy: string | undefined;
  onQueryChange: (value: string) => void;
  onKeyDown: (e: ReactKeyboardEvent<HTMLElement>) => void;
  onOptionHover: (i: number) => void;
  onOptionClick: (opt: SelectOption<V>) => void;
  renderEmpty: ((query: string) => ReactNode) | undefined;
  panelRef: Ref<HTMLDivElement>;
  searchInputRef: Ref<HTMLInputElement>;
}

/** Pure presentational panel — all state logic lives in <Select>.
 *  Isolated so Select.tsx stays under the 500-line budget. */
export function SelectPanel<V extends string = string>(props: SelectPanelProps<V>): ReactNode {
  const t = useI18nT();
  const {
    panelId,
    triggerId,
    floatingStyle,
    maxHeight,
    placement,
    mobileSheet,
    options,
    groupOrder,
    visibleCount,
    highlightIndex,
    selectedIndex,
    searchable,
    searchPlaceholder,
    query,
    ariaLabelledBy,
    onQueryChange,
    onKeyDown,
    onOptionHover,
    onOptionClick,
    renderEmpty,
    panelRef,
    searchInputRef,
  } = props;

  const activeDescendantId =
    highlightIndex >= 0 ? `${panelId}-opt-${String(highlightIndex)}` : undefined;

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>): void => {
    onQueryChange(e.target.value);
  };

  return (
    <div
      ref={panelRef}
      id={panelId}
      className="vq-select__panel"
      data-component="SelectPanel"
      data-placement={placement}
      data-mobile-sheet={mobileSheet ? 'true' : undefined}
      data-searchable={searchable ? 'true' : undefined}
      style={
        mobileSheet
          ? MOBILE_FLOATING_PANEL_STYLE
          : {
              ...floatingStyle,
              maxHeight: `${String(maxHeight)}px`,
            }
      }
      onKeyDown={onKeyDown}
    >
      {searchable ? (
        <div className="vq-select__search" data-slot="search">
          <input
            ref={searchInputRef}
            type="text"
            role="searchbox"
            className="vq-select__search-input"
            placeholder={searchPlaceholder}
            value={query}
            onChange={handleSearchChange}
            aria-controls={panelId}
            aria-autocomplete="list"
            aria-activedescendant={activeDescendantId}
          />
        </div>
      ) : null}

      <ul
        role="listbox"
        aria-labelledby={ariaLabelledBy ?? triggerId}
        className="vq-select__options"
        data-slot="options"
      >
        {groupOrder.map(({ group, optionIndices }) => (
          <li
            key={group === null ? '__ungrouped' : group.id}
            role="group"
            aria-label={group !== null ? optionLabelText(group.label) : undefined}
            className="vq-select__group"
          >
            {group !== null ? (
              <div className="vq-select__group-header" role="presentation">
                {group.label}
              </div>
            ) : null}
            <ul className="vq-select__group-body" role="presentation">
              {optionIndices.map((i) => {
                const opt = options[i];
                if (opt === undefined) return null;
                const isSelected = i === selectedIndex;
                const isHighlighted = i === highlightIndex;
                const optId = `${panelId}-opt-${String(i)}`;
                return (
                  <li
                    key={opt.value}
                    id={optId}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={opt.disabled === true || undefined}
                    data-option-index={i}
                    data-highlighted={isHighlighted ? 'true' : undefined}
                    data-disabled={opt.disabled === true ? 'true' : undefined}
                    className="vq-select__option"
                    onMouseEnter={() => {
                      if (opt.disabled !== true) onOptionHover(i);
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                    }}
                    onClick={() => {
                      if (opt.disabled === true) return;
                      onOptionClick(opt);
                    }}
                  >
                    <span className="vq-select__option-check" aria-hidden="true">
                      {isSelected ? <IcnCheck size={12} /> : null}
                    </span>
                    <span className="vq-select__option-label">
                      {opt.icon !== undefined ? (
                        <span className="vq-select__option-icon" aria-hidden="true">
                          {opt.icon}
                        </span>
                      ) : null}
                      <span className="vq-select__option-text">{opt.label}</span>
                      {opt.description !== undefined ? (
                        <span className="vq-select__option-desc">{opt.description}</span>
                      ) : null}
                    </span>
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ul>

      {visibleCount === 0 ? (
        <div className="vq-select__empty" role="status">
          {renderEmpty !== undefined
            ? renderEmpty(query)
            : query.length > 0
              ? t('select.noMatch', { query })
              : t('select.empty')}
        </div>
      ) : null}
    </div>
  );
}
